/**
 * Flow Validation
 * Ensures flows created in the frontend match the runtime schema exactly
 */

import type {
  FlowNode as SharedFlowNode,
  NodeType,
  NodeData,
  ATCommandData,
  ConditionData,
  ParallelData,
  LoopData,
  DelayData,
  AssertionData,
  PrimitiveCallData,
  VariableData,
  DataTestData,
  VoiceTestData,
  VoiceAgentTestData,
} from '@simcheck/shared-types';

// Frontend FlowNode extends shared type with position
interface FlowNode extends Omit<SharedFlowNode, 'position'> {
  position: { x: number; y: number };
}

interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

interface Flow {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface ValidationError {
  nodeId?: string;
  edgeId?: string;
  type: 'error' | 'warning';
  message: string;
  field?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

/**
 * Validate entire flow
 */
export function validateFlow(flow: Flow): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // 1. Structural validation
  validateStructure(flow, errors);

  // 2. Validate each node
  flow.nodes.forEach(node => {
    validateNode(node, errors, warnings);
  });

  // 3. Validate edges
  flow.edges.forEach(edge => {
    validateEdge(edge, flow.nodes, errors);
  });

  // 4. Validate flow connectivity
  validateConnectivity(flow, errors, warnings);

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate flow structure (start/end nodes, etc.)
 */
function validateStructure(flow: Flow, errors: ValidationError[]): void {
  // Must have at least one node
  if (flow.nodes.length === 0) {
    errors.push({
      type: 'error',
      message: 'Flow must have at least one node',
    });
    return;
  }

  // Must have exactly one start node
  const startNodes = flow.nodes.filter(n => n.type === 'start');
  if (startNodes.length === 0) {
    errors.push({
      type: 'error',
      message: 'Flow must have exactly one start node',
    });
  } else if (startNodes.length > 1) {
    errors.push({
      type: 'error',
      message: `Flow has ${startNodes.length} start nodes, but must have exactly one`,
    });
  }

  // Must have at least one end node
  const endNodes = flow.nodes.filter(n => n.type === 'end');
  if (endNodes.length === 0) {
    errors.push({
      type: 'error',
      message: 'Flow must have at least one end node',
    });
  }
}

/**
 * Validate individual node
 */
function validateNode(node: FlowNode, errors: ValidationError[], warnings: ValidationError[]): void {
  // Basic fields
  if (!node.id) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Node must have an id',
      field: 'id',
    });
  }

  if (!node.type) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Node must have a type',
      field: 'type',
    });
    return;
  }

  // Only warn about missing labels for non-structural nodes
  // Start and end nodes are self-explanatory and don't need custom labels
  if (!node.label && node.type !== 'start' && node.type !== 'end') {
    warnings.push({
      nodeId: node.id,
      type: 'warning',
      message: 'Node should have a label',
      field: 'label',
    });
  }

  if (!node.data) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Node must have a data object',
      field: 'data',
    });
    return;
  }

  // `log` is a valid runtime node (LogExecutor) that predates the shared
  // NodeType union, so it isn't a member of the typed switch below. Treat it as
  // known — it carries a free-form message with nothing to require — rather than
  // letting it fall through to "Unknown node type".
  if ((node.type as string) === 'log') return;

  // Type-specific validation
  switch (node.type) {
    case 'start':
    case 'end':
      validateStartEndNode(node, errors);
      break;
    case 'atCommand':
    case 'at_command':
      validateATCommandNode(node, errors, warnings);
      break;
    case 'delay':
      validateDelayNode(node, errors);
      break;
    case 'condition':
      validateConditionNode(node, errors);
      break;
    case 'loop':
      validateLoopNode(node, errors);
      break;
    case 'parallel':
      validateParallelNode(node, errors);
      break;
    case 'variable':
      validateVariableNode(node, errors);
      break;
    case 'assertion':
      validateAssertionNode(node, errors);
      break;
    case 'primitiveCall':
      validatePrimitiveCallNode(node, errors);
      break;
    case 'dataTest':
      validateDataTestNode(node, errors);
      break;
    case 'voiceTest':
      validateVoiceTestNode(node, errors);
      break;
    case 'voiceAgentTest':
      validateVoiceAgentTestNode(node, errors);
      break;
    case 'api_call':
      // API call validation can be added here
      break;
    case 'transform':
      // Transform validation can be added here
      break;
    default:
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: `Unknown node type: ${node.type}`,
        field: 'type',
      });
  }
}

/**
 * Validate start/end nodes
 */
function validateStartEndNode(node: FlowNode, errors: ValidationError[]): void {
  const dataKeys = Object.keys(node.data);

  // Start nodes should have empty data
  if (node.type === 'start') {
    if (dataKeys.length > 0) {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: 'start node should have empty data object',
        field: 'data',
      });
    }
    return;
  }

  // End nodes accept any combination of the following optional keys:
  //   - `summary`   — author-written template rendered by FlowExecutor as the
  //                   test run summary (see FlowExecutor.ts).
  //   - `end`       — MCP-format `{ status, message }` payload.
  // Empty data is also valid. Anything else is rejected.
  if (node.type === 'end') {
    if (dataKeys.length === 0) return;

    const data = node.data as Record<string, unknown>;
    const allowedKeys = new Set(['summary', 'end']);
    const unknownKeys = dataKeys.filter((k) => !allowedKeys.has(k));

    if (unknownKeys.length > 0) {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: `end node has unsupported data keys: ${unknownKeys.join(', ')}. Allowed: summary, end.`,
        field: 'data',
      });
      return;
    }

    if ('summary' in data && data.summary !== undefined && typeof data.summary !== 'string') {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: 'end node summary must be a string template',
        field: 'data.summary',
      });
    }

    if ('end' in data && data.end !== undefined) {
      const endData = data.end as { status?: string; message?: string } | undefined;
      if (endData && endData.status && !['success', 'failure', 'error'].includes(endData.status)) {
        errors.push({
          nodeId: node.id,
          type: 'error',
          message: `end node status must be 'success', 'failure', or 'error'`,
          field: 'data.end.status',
        });
      }
    }
  }
}

/**
 * Validate AT Command node
 */
function validateATCommandNode(node: FlowNode, errors: ValidationError[], warnings: ValidationError[]): void {
  const { data } = node;

  // Check for correct nested structure
  if (!data.atCommand) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'atCommand node must have data.atCommand property',
      field: 'data.atCommand',
    });
    return;
  }

  const atData = data.atCommand as ATCommandData;

  // Required fields
  if (!atData.command || atData.command.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AT Command must have a command',
      field: 'data.atCommand.command',
    });
  } else {
    // Validate AT command format
    const cmd = atData.command.trim();
    
    // Must start with "AT"
    if (!cmd.toUpperCase().startsWith('AT')) {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: 'AT Command must start with "AT"',
        field: 'data.atCommand.command',
      });
    }
    
    // Must be more than just "AT" - needs actual command
    if (cmd.toUpperCase() === 'AT' || cmd.toUpperCase() === 'AT+' || cmd.toUpperCase() === 'AT&') {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: 'AT Command is incomplete - specify the full command (e.g., AT+CSQ, AT+COPS?)',
        field: 'data.atCommand.command',
      });
    }
    
    // Note: Removed redundant length check that was rejecting valid 3-char commands like ATI, ATE, ATZ
    // The check above already validates incomplete commands like "AT", "AT+", "AT&"
  }

  if (typeof atData.timeout !== 'number' || atData.timeout <= 0) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AT Command must have a positive timeout (milliseconds)',
      field: 'data.atCommand.timeout',
    });
  }

  if (typeof atData.retries !== 'number' || atData.retries < 0) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AT Command must have retries >= 0',
      field: 'data.atCommand.retries',
    });
  }

  if (typeof atData.parseResponse !== 'boolean') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AT Command must have parseResponse as boolean',
      field: 'data.atCommand.parseResponse',
    });
  }

  // Expected response is required unless acceptAnyResponse is true
  if (!atData.acceptAnyResponse && (!atData.expectedResponse || atData.expectedResponse.trim() === '')) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AT Command must have an expected response (e.g., "OK", "+CSQ:") or enable "Accept Any Response"',
      field: 'data.atCommand.expectedResponse',
    });
  }
  
  // Warnings
  if (atData.retries === 0) {
    warnings.push({
      nodeId: node.id,
      type: 'warning',
      message: 'AT Command has 0 retries - consider setting at least 1 retry for reliability',
      field: 'data.atCommand.retries',
    });
  }

  if (atData.timeout > 30000) {
    warnings.push({
      nodeId: node.id,
      type: 'warning',
      message: 'AT Command timeout is very long (>30s)',
      field: 'data.atCommand.timeout',
    });
  }
}

/**
 * Validate Data Test node (primitive-only). Requires a testType; per-sub-type
 * params are validated server-side by the edge dispatcher, so we keep the
 * frontend gate minimal — just enough to catch an unconfigured node.
 */
function validateDataTestNode(node: FlowNode, errors: ValidationError[]): void {
  const dt = node.data.dataTest as DataTestData | undefined;
  if (!dt) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'dataTest node must have data.dataTest property',
      field: 'data.dataTest',
    });
    return;
  }
  if (!dt.testType || String(dt.testType).trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Data Test must have a testType (e.g., ping, http, dns, sftp)',
      field: 'data.dataTest.testType',
    });
  }
}

/**
 * Validate Voice Test node (primitive-only). Requires a kind; bearer/codec/DTMF
 * params are sub-type-specific and validated by the edge voice runner.
 */
function validateVoiceTestNode(node: FlowNode, errors: ValidationError[]): void {
  const vt = node.data.voiceTest as VoiceTestData | undefined;
  if (!vt) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'voiceTest node must have data.voiceTest property',
      field: 'data.voiceTest',
    });
    return;
  }
  if (!vt.kind || String(vt.kind).trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Voice Test must have a kind (e.g., ims_status, call_basic, send_dtmf)',
      field: 'data.voiceTest.kind',
    });
  }
}

/**
 * Validate Voice Agent Test node (primitive-only). Requires a phoneNumber to
 * dial and a goal for the agent to pursue; the agent behaviour / assertions are
 * enforced server-side by the voice-agent service.
 */
function validateVoiceAgentTestNode(node: FlowNode, errors: ValidationError[]): void {
  const va = node.data.voiceAgentTest as VoiceAgentTestData | undefined;
  if (!va) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'voiceAgentTest node must have data.voiceAgentTest property',
      field: 'data.voiceAgentTest',
    });
    return;
  }
  if (!va.phoneNumber || String(va.phoneNumber).trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AI Agent Call must have a phoneNumber to dial',
      field: 'data.voiceAgentTest.phoneNumber',
    });
  }
  const goal = va.agentConfig?.goal;
  if (!goal || String(goal).trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'AI Agent Call must have agent instructions (agentConfig.goal)',
      field: 'data.voiceAgentTest.agentConfig.goal',
    });
  }
}

/**
 * Validate Delay node
 */
function validateDelayNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.delay) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'delay node must have data.delay property',
      field: 'data.delay',
    });
    return;
  }

  const delayData = data.delay as DelayData;

  if (typeof delayData.duration !== 'number' || delayData.duration <= 0) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Delay must have a positive duration',
      field: 'data.delay.duration',
    });
  }

  const validUnits = ['ms', 's', 'm'];
  if (!validUnits.includes(delayData.unit)) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: `Delay unit must be one of: ${validUnits.join(', ')}. Got: ${delayData.unit}`,
      field: 'data.delay.unit',
    });
  }
}

/**
 * Validate Condition node
 */
function validateConditionNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.condition) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'condition node must have data.condition property',
      field: 'data.condition',
    });
    return;
  }

  const condData = data.condition as ConditionData;

  if (!condData.expression || condData.expression.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Condition must have an expression',
      field: 'data.condition.expression',
    });
  }
}

/**
 * Validate Loop node
 */
function validateLoopNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.loop) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'loop node must have data.loop property',
      field: 'data.loop',
    });
    return;
  }

  const loopData = data.loop as LoopData;

  if (!loopData.iteratorExpression || loopData.iteratorExpression.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Loop must have an iteratorExpression',
      field: 'data.loop.iteratorExpression',
    });
  }

  if (!loopData.itemVariable || loopData.itemVariable.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Loop must have an itemVariable',
      field: 'data.loop.itemVariable',
    });
  }

  if (!loopData.indexVariable || loopData.indexVariable.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Loop must have an indexVariable',
      field: 'data.loop.indexVariable',
    });
  }

  if (loopData.maxIterations !== undefined) {
    if (typeof loopData.maxIterations !== 'number' || loopData.maxIterations <= 0) {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: 'Loop maxIterations must be a positive number',
        field: 'data.loop.maxIterations',
      });
    }
  }
}

/**
 * Validate Parallel node
 */
function validateParallelNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.parallel) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'parallel node must have data.parallel property',
      field: 'data.parallel',
    });
    return;
  }

  const parallelData = data.parallel as ParallelData;

  if (!Array.isArray(parallelData.branches) || parallelData.branches.length === 0) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Parallel must have at least one branch',
      field: 'data.parallel.branches',
    });
  }

  if (typeof parallelData.waitForAll !== 'boolean') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Parallel must have waitForAll as boolean',
      field: 'data.parallel.waitForAll',
    });
  }
}

/**
 * Validate Variable node
 */
function validateVariableNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.variable) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'variable node must have data.variable property',
      field: 'data.variable',
    });
    return;
  }

  const varData = data.variable as VariableData;

  if (!varData.name || varData.name.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Variable must have a name',
      field: 'data.variable.name',
    });
  }

  if (varData.operation === 'random_choice') {
    // value is a list (array, or newline/comma-separated string) to pick from
    const list = Array.isArray(varData.value)
      ? varData.value
      : typeof varData.value === 'string'
        ? varData.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
        : [];
    if (list.length === 0) {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: 'random_choice variable must have a non-empty list of options',
        field: 'data.variable.value',
      });
    }
  } else if (varData.value === undefined) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Variable must have a value',
      field: 'data.variable.value',
    });
  }

  if (varData.type) {
    const validTypes = ['string', 'number', 'boolean', 'object', 'array'];
    if (!validTypes.includes(varData.type)) {
      errors.push({
        nodeId: node.id,
        type: 'error',
        message: `Variable type must be one of: ${validTypes.join(', ')}`,
        field: 'data.variable.type',
      });
    }
  }
}

/**
 * Validate Assertion node
 */
function validateAssertionNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.assertion) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'assertion node must have data.assertion property',
      field: 'data.assertion',
    });
    return;
  }

  const assertData = data.assertion as AssertionData;

  if (!assertData.expression || assertData.expression.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Assertion must have an expression',
      field: 'data.assertion.expression',
    });
  }

  if (!assertData.message || assertData.message.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Assertion must have a message',
      field: 'data.assertion.message',
    });
  }

  const validSeverities = ['error', 'warning', 'info'];
  if (!validSeverities.includes(assertData.severity)) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: `Assertion severity must be one of: ${validSeverities.join(', ')}`,
      field: 'data.assertion.severity',
    });
  }
}

/**
 * Validate Primitive Call node
 */
function validatePrimitiveCallNode(node: FlowNode, errors: ValidationError[]): void {
  const { data } = node;

  if (!data.primitiveCall) {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'primitiveCall node must have data.primitiveCall property',
      field: 'data.primitiveCall',
    });
    return;
  }

  const primData = data.primitiveCall as PrimitiveCallData;

  if (!primData.primitiveId || primData.primitiveId.trim() === '') {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'Primitive Call must have a primitiveId',
      field: 'data.primitiveCall.primitiveId',
    });
  }
}

/**
 * Validate edges
 */
function validateEdge(edge: FlowEdge, nodes: FlowNode[], errors: ValidationError[]): void {
  if (!edge.id) {
    errors.push({
      edgeId: edge.id,
      type: 'error',
      message: 'Edge must have an id',
    });
  }

  // Check source node exists
  const sourceNode = nodes.find(n => n.id === edge.source);
  if (!sourceNode) {
    errors.push({
      edgeId: edge.id,
      type: 'error',
      message: `Edge source node not found: ${edge.source}`,
    });
  }

  // Check target node exists
  const targetNode = nodes.find(n => n.id === edge.target);
  if (!targetNode) {
    errors.push({
      edgeId: edge.id,
      type: 'error',
      message: `Edge target node not found: ${edge.target}`,
    });
  }
}

/**
 * Validate flow connectivity
 */
function validateConnectivity(flow: Flow, errors: ValidationError[], warnings: ValidationError[]): void {
  const { nodes, edges } = flow;

  // Find start node
  const startNode = nodes.find(n => n.type === 'start');
  if (!startNode) return; // Already caught in structure validation

  // Build adjacency list
  const adjacency = new Map<string, string[]>();
  edges.forEach(edge => {
    const targets = adjacency.get(edge.source) || [];
    targets.push(edge.target);
    adjacency.set(edge.source, targets);
  });

  // DFS to find reachable nodes
  const reachable = new Set<string>();
  const visited = new Set<string>();

  function dfs(nodeId: string) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    reachable.add(nodeId);

    const targets = adjacency.get(nodeId) || [];
    targets.forEach(target => dfs(target));
  }

  dfs(startNode.id);

  // Find orphaned nodes (not reachable from start)
  const orphanedNodes = nodes.filter(n => 
    n.type !== 'start' && !reachable.has(n.id)
  );

  orphanedNodes.forEach(node => {
    warnings.push({
      nodeId: node.id,
      type: 'warning',
      message: `Node "${node.label || node.id}" is not reachable from start`,
    });
  });

  // Check if end nodes are reachable
  const endNodes = nodes.filter(n => n.type === 'end');
  const unreachableEndNodes = endNodes.filter(n => !reachable.has(n.id));

  unreachableEndNodes.forEach(node => {
    errors.push({
      nodeId: node.id,
      type: 'error',
      message: 'End node is not reachable from start',
    });
  });

  // Warn about nodes with no outgoing edges (except end nodes)
  nodes.forEach(node => {
    if (node.type === 'end') return;
    
    const hasOutgoing = edges.some(e => e.source === node.id);
    if (!hasOutgoing) {
      warnings.push({
        nodeId: node.id,
        type: 'warning',
        message: `Node "${node.label || node.id}" has no outgoing connections`,
      });
    }
  });
}

/**
 * Quick validation - just check if node has correct structure
 */
export function quickValidateNode(node: Partial<FlowNode>): boolean {
  if (!node.type || !node.data) return false;

  switch (node.type) {
    case 'start':
    case 'end':
      return true;
    case 'atCommand':
      return !!node.data.atCommand;
    case 'delay':
      return !!node.data.delay;
    case 'condition':
      return !!node.data.condition;
    case 'loop':
      return !!node.data.loop;
    case 'parallel':
      return !!node.data.parallel;
    case 'variable':
      return !!node.data.variable;
    case 'assertion':
      return !!node.data.assertion;
    case 'primitiveCall':
      return !!node.data.primitiveCall;
    case 'transform':
      return !!(node.data as Record<string, unknown>).transform;
    default:
      // For unknown types, just check that data exists
      return true;
  }
}

